import { useState } from "react";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type ITripTerminus,
  type InputErrorType,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FlightLocationAutocomplete,
  FlightDateSelection,
} from "@tenant/flights";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "../FlightSearch.module.scss";
import { TenantIconNameEnum } from "@/types/tenantIcons";

interface FlightSearchRowProps {
  onOriginChange: (origin: ITripTerminus) => void;
  onDestinationChange: (destination: ITripTerminus) => void;
  onOriginFocus?: () => void;
  onDestinationFocus?: () => void;
  selectedOrigin: ITripTerminus | undefined;
  selectedDestination: ITripTerminus | undefined;
  errors?: {
    origin?: InputErrorType;
    destination?: InputErrorType;
    dates?: InputErrorType;
  };
  index?: number;
  isMulticity?: boolean;
  onSwap?: () => void;
}

export const FlightSearchRow = ({
  onOriginChange,
  onDestinationChange,
  onOriginFocus,
  onDestinationFocus,
  selectedOrigin,
  selectedDestination,
  errors,
  index,
  isMulticity = false,
  onSwap,
}: FlightSearchRowProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.FlightSearch;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();
  const [rotationDegrees, setRotationDegrees] = useState(0);

  const showSwapButton = !isMulticity;
  const isSwapDisabled = !selectedOrigin || !selectedDestination;

  const handleSwapClick = () => {
    if (!isSwapDisabled) {
      // Use modulo to keep rotation bounded within 0-360
      setRotationDegrees((prev) => (prev + 180) % 360);
      onSwap?.();
    }
  };

  return (
    <>
      <div
        className={clsx(cn("origin-selection-container"), cn("cell-content"))}
      >
        <FlightLocationAutocomplete
          id="origin-selection-autocomplete"
          onChange={onOriginChange}
          onFocus={onOriginFocus}
          value={selectedOrigin}
          label={t("searchControl.whereFrom")}
          placeholder={t("core-flights.search.placeholder")}
          error={errors?.origin}
        />
      </div>
      {showSwapButton && (
        <button
          type="button"
          onClick={handleSwapClick}
          disabled={isSwapDisabled}
          className={cn("swap-button")}
          aria-label={t("core-flights.search.swapLocations")}
          aria-disabled={isSwapDisabled}
          style={
            { "--rotation": `${rotationDegrees}deg` } as React.CSSProperties
          }
        >
          <Icon iconName={TenantIconNameEnum.swap} />
        </button>
      )}
      <div
        className={clsx(
          cn("destination-selection-container"),
          cn("cell-content")
        )}
      >
        <FlightLocationAutocomplete
          id="destination-selection-autocomplete"
          onChange={onDestinationChange}
          onFocus={onDestinationFocus}
          value={selectedDestination}
          label={t("searchControl.whereTo")}
          placeholder={t("core-flights.search.placeholder")}
          error={errors?.destination}
        />
      </div>
      <div
        className={clsx(
          cn("dates-selection-container", { multi_city: isMulticity }),
          cn("cell-content")
        )}
      >
        <FlightDateSelection index={index} error={errors?.dates} />
      </div>
    </>
  );
};
